import {
  conversationSections,
  conversationSectionsStatus,
  conversationSectionsSubscriptionsStatus,
  sidebarSectionDetailModal,
} from 'conversations/modules/user/sections/reducer'
import { isObject } from 'conversations/utils/typescriptHelpers'
import produce from 'immer'
import merge from 'lodash/merge'
import { combineReducers } from 'redux'
import { receiveGatesUpdate } from 'shared/store/actions/gates'
import {
  fetchInboxesAsync,
  fetchNotificationSettingsBySubscription,
  fetchPreferencesAsync,
  fetchUsersAsync,
  patchNotificationSettingsBySubscription,
  receiveUser,
  setActiveInbox,
  setDraftPreference,
  setPreference,
  setPreferenceOff,
  setPreferenceOn,
  togglePreference,
  trafficControlUpdateNotificationSettingsBySubscription,
  updateNotificationSettingsBySubscription,
  updateUserPreferenceAsync,
  UserPreference,
} from 'shared/store/actions/user'
import { SectionNotificationSetting } from 'shared/typings/conversationsSidebar/Filters'
import Inbox from 'shared/typings/inboxes/Inbox'
import { User } from 'shared/typings/user/User'
import { localStore } from 'shared/utils/storage'

import { createReducer, RootAction } from 'typesafe-actions'

type UsersById = Record<number, User | undefined>

const currentUserPreferences = createReducer<Record<UserPreference, any>>({
  showQuickReplies: true,
  useSendButton: false,
  onboarding: {},
  conversationSidebarFilters: '',
  onBoardingV2Dismissed: undefined,
  hasMobileBannerDismissed: undefined,
  lastMarkAllAsRead: [],
  convoAnalysisMoveDismissed: false,
  responseTimerSettingDismissed: false,
  aiSuggestedRepliesDismissed: false,
  aiTranslationsDismissed: false,
  autoAwayLastSeen: 0,
})
  .handleAction(fetchPreferencesAsync.success, (state, action) => merge(state, action.payload))
  .handleAction(togglePreference, (state, action) =>
    produce(state, (draft: any) => {
      draft[action.payload] = !draft[action.payload]
    })
  )
  .handleAction(setPreferenceOn, (state, action) =>
    produce(state, (draft: any) => {
      draft[action.payload] = true
    })
  )
  .handleAction(setPreferenceOff, (state, action) =>
    produce(state, (draft: any) => {
      draft[action.payload] = false
    })
  )
  .handleAction(setPreference, (state, action) =>
    produce(state, (draft: any) => {
      const prefState = draft[action.payload.preference]
      const payloadValue = action.payload.value
      if (isObject(prefState) && isObject(payloadValue) && !Array.isArray(payloadValue)) {
        draft[action.payload.preference] = {
          ...prefState,
          ...payloadValue,
        }
      } else {
        draft[action.payload.preference] = payloadValue
      }
    })
  )
  .handleAction(setDraftPreference, (state, action) =>
    produce(state, (draft: any) => {
      const prefState = draft[action.payload.preference]
      const payloadValue = action.payload.value
      if (isObject(prefState) && isObject(payloadValue) && !Array.isArray(payloadValue)) {
        draft[action.payload.preference] = {
          ...prefState,
          ...payloadValue,
        }
      } else {
        draft[action.payload.preference] = payloadValue
      }
    })
  )
  .handleAction(
    [updateUserPreferenceAsync.success, updateUserPreferenceAsync.failure],
    (state, action) =>
      produce(state, (draft: any) => {
        const prefState = draft[action.payload.preference]
        const payloadValue = action.payload.value
        if (isObject(prefState) && isObject(payloadValue) && !Array.isArray(payloadValue)) {
          draft[action.payload.preference] = {
            ...prefState,
            ...payloadValue,
          }
        } else {
          draft[action.payload.preference] = payloadValue
        }
      })
  )

const currentUserPreferencesStatus = createReducer<null | string>(null).handleAction(
  [
    fetchPreferencesAsync.success,
    fetchPreferencesAsync.request,
    fetchPreferencesAsync.cancel,
    fetchPreferencesAsync.failure,
  ],
  (_, action) => action.type
)

const byId = createReducer<UsersById>({})
  .handleAction(fetchUsersAsync.success, (state, action) => {
    return produce(state, (draft) => {
      action.payload.forEach((user: any) => {
        draft[user.id] = user
      })
    })
  })
  .handleAction(receiveUser, (state, action) => {
    const user = action.payload
    return produce(state, (draft) => {
      // Do an upsert on the user so we don't wipe out gates and properties
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore-next-line
      draft[user.id] = draft[user.id] ? Object.assign(draft[user.id], user) : user
    })
  })
  .handleAction(receiveGatesUpdate, (state, action) => {
    const [userId, gates] = action.payload
    return produce(state, (draft) => {
      const user = draft[userId]
      if (user) {
        user.gates = gates
      }
    })
  })

const currentUserNotificationsBySubscription = createReducer<null | Record<
  string,
  SectionNotificationSetting
>>(null).handleAction(
  [fetchNotificationSettingsBySubscription, patchNotificationSettingsBySubscription],
  (_, action) => action.payload
)

const updatedUserNotificationsBySubscription = createReducer<
  Record<string, SectionNotificationSetting>
>({})
  .handleAction([updateNotificationSettingsBySubscription], (state, action) => {
    const sidebarFilter = action.payload
    return produce(state, (draft) => {
      const { subscriptionId } = sidebarFilter
      if (subscriptionId) {
        const updatedSetting = draft[subscriptionId] || { subscriptionId }
        updatedSetting.allNotificationsForSubscriptionEnabled =
          !updatedSetting.allNotificationsForSubscriptionEnabled
        draft[subscriptionId] = updatedSetting
      }
    })
  })
  .handleAction(
    [
      fetchNotificationSettingsBySubscription,
      patchNotificationSettingsBySubscription,
      trafficControlUpdateNotificationSettingsBySubscription,
    ],
    (_, action) => action.payload
  )

const activeInbox = createReducer<Inbox | null, RootAction>(null)
  .handleAction([setActiveInbox], (_, action) => action.payload)
  .handleAction(fetchInboxesAsync.success, (state, action) => {
    return produce(state, (draft) => {
      if (draft) {
        const inboxes = action.payload
        const found = inboxes.find((inbox: Inbox) => inbox.id === draft.id)
        const inboxToSet = found ? draft : null
        localStore.set('cd_active_inbox', JSON.stringify(inboxToSet))
        return inboxToSet
      }
      return draft
    })
  })

export default combineReducers({
  byId,
  conversationSections,
  conversationSectionsStatus,
  sidebarSectionDetailModal,
  currentUserPreferences,
  currentUserPreferencesStatus,
  currentUserNotificationsBySubscription,
  updatedUserNotificationsBySubscription,
  conversationSectionsSubscriptionsStatus,
  activeInbox,
})
